// ────────────────────────────────────────────────────────────
// quotes — 전세버스 견적 접수 (통근·통학 / 일반 관광 · 2026-10-18 way)
//
//   홈페이지 구글폼 2종(「동영_홈페이지배차접수(통근통학)」·「dy_홈페이지배차접수(일반)」)을 DYCS 로 옮긴 것.
//
// 🔴 **tickets 와 다른 컬렉션이다** — 문의·분실물 티켓 컬렉션엔 onCreate/onUpdate 알림 트리거가 10종 넘게
//    걸려 있다(CS 스페이스·영업 스페이스·전사 공지·담당자 알림톡·웹푸시·미회신 독촉·고객 알림톡…).
//    견적을 티켓 type 으로 넣으면 그 전부가 견적에도 울리고, 과거분 767건 적재 때 트리거를 하나씩
//    가드해야 한다. 견적은 진행 단계(견적발송·배차진행·미진행)도, 받는 사람(영업)도 다르다.
//    → 별도 컬렉션 + 전용 트리거 1개(`onQuoteCreated` → 견적 구글챗 스페이스)만 둔다.
// ────────────────────────────────────────────────────────────

import type { TimestampLike } from './types';

/** 견적 구분 — commute = 통근·통학(정기 노선) · tour = 일반 관광(1회 운행). */
export type QuoteKind = 'commute' | 'tour';

/**
 * 진행 단계. 구 시트 처리결과 값 → 단계:
 *   (빈칸) → received · 견적발송 → quoted · 배차진행/계약 → booked · 오더진행안됨 → lost.
 *   contacting = 고객과 통화·조율 중(구 시트엔 없던 칸 — 「누가 잡고 있나」가 보이게).
 */
export type QuoteStage = 'received' | 'contacting' | 'quoted' | 'booked' | 'lost';

export type QuoteSource = 'web' | 'migrated';

export interface Quote {
  id: string;
  /** 접수 창구 거래처(홈페이지 = 'homepage'). 🔴 멀티테넌트 스코프 — rules 가 이 값으로 가린다. */
  tenantId: string;
  kind: QuoteKind;
  stage: QuoteStage;
  source: QuoteSource;

  // 신청인
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  /** 단체·회사·학교명. */
  companyName?: string;

  // 차량
  /** 차량 종류(QUOTE_VEHICLE_TYPES 값 또는 구 시트 원문). */
  vehicleType?: string;
  /** 차량 대수(선택 · 신규 폼부터). */
  vehicleCount?: number;
  /** 탑승 인원(선택 · 신규 폼부터). */
  passengers?: number;

  // commute 전용
  /** 노선 지역(예: 「판교 ↔ 강남」). */
  routeRegion?: string;
  /** 노선 개수(원문 그대로 — 구 시트에 「2~3개」 같은 값이 있어 숫자로 강제하지 않는다). */
  routeCount?: string;
  /** 운행 시작 희망일(선택 · 신규 폼부터). */
  startDate?: string;

  // tour 전용
  departRegion?: string;
  /** YYYY-MM-DD. */
  departDate?: string;
  arriveRegion?: string;
  arriveDate?: string;

  // 공통 시간(commute = 출근 출발·도착 / tour = 출발·도착)
  departTime?: string;
  arriveTime?: string;

  /** 동영관광을 알게 된 경로. */
  referral?: string;
  /** 요청사항·추가설명. */
  note?: string;
  privacyConsent: boolean;

  // 사내 처리
  /** 영업 담당(이름 · 구 시트 「영업담당자」 원문 포함). */
  salesOwner?: string;
  /** 사내 메모(구 시트 비고·처리진행 포함). 🔴 고객에게 나가지 않는다. */
  memo?: string;

  // 이관
  /** 구 시트 행 식별(NO 또는 타임스탬프 원문). */
  legacyNo?: string;
  /** 구 시트 타임스탬프 원문(파싱 전). */
  legacyTimestampText?: string;
  /** 구 시트 처리결과 원문(단계로 접기 전 — 상세에 그대로 보인다). */
  legacyResult?: string;
  isTest?: boolean;

  /** 견적 스페이스 카드 발송 선점 시각(onQuoteCreated 멱등 플래그). */
  chatNotifiedAt?: TimestampLike;
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
  updatedBy?: string;
}

export const QUOTE_KIND_LABELS: Record<QuoteKind, string> = {
  commute: '통근·통학',
  tour: '일반 관광',
};

export const QUOTE_STAGE_LABELS: Record<QuoteStage, string> = {
  received: '접수',
  contacting: '상담중',
  quoted: '견적발송',
  booked: '배차진행',
  lost: '미진행',
};

/** 화면 진행 순서(상세의 단계 버튼 · 목록 필터). */
export const QUOTE_STAGE_FLOW: QuoteStage[] = ['received', 'contacting', 'quoted', 'booked', 'lost'];

/** 아직 손이 필요한 단계(목록 기본 필터 · 좌 레일 카운트). */
export const QUOTE_OPEN_STAGES: QuoteStage[] = ['received', 'contacting'];

/** 차량 종류 선택지 — 구 구글폼 선택지 그대로. */
export const QUOTE_VEHICLE_TYPES: string[] = ['45인승', '25인승', '리무진 28인승', '잘 모르겠어요'];

/** 알게 된 경로 선택지 — 구 구글폼 응답 상위값. */
export const QUOTE_REFERRAL_OPTIONS: string[] = [
  '인터넷 검색',
  '지인 소개',
  '기존 이용 고객',
  '홈페이지',
  '기타',
];

/** 구 시트 처리결과 원문 → 단계. 모르는 값은 null(호출부가 received 로 두고 원문을 legacyResult 에 남긴다). */
//   실측(2026-10-18 · 두 시트 767건): 관광 = 배차진행 249 · 견적발송 202 · 오더진행안됨 6 · 「입력」 167(빈 자리).
//   통근 = 자유 서술(「차량수급 어려움」·「견적서 발송후 내부검토중」·「타업체 선정」…).
//   🔴 «운행» 은 booked 신호로 쓰지 않는다 — 「견적서 발송 *1~3개월 단기운행」 이 수주로 잘못 접힌다.
//   ⚠️ scripts/migrate-quotes.cjs 가 같은 규칙을 미러한다 — 한쪽만 고치지 말 것.
export function stageFromLegacyResult(raw: string | undefined | null): QuoteStage | null {
  const s = (raw || '').replace(/\s+/g, '');
  if (!s || s === '.' || s === '입력' || s === '과거처리') return null;
  if (/진행안됨|미진행|비매칭|불가|어려움|타업체|보류|연기|미정|회신없음|취소|실패/.test(s)) return 'lost';
  if (/배차진행|계약|수주|확정/.test(s)) return 'booked';
  if (/견적/.test(s)) return 'quoted';
  if (/통화|상담|확인|검토|연락|미팅|방문|응대/.test(s)) return 'contacting';
  return null;
}

/** 견적 한 줄 요약(목록·카드 공용) — 통근 = 노선 지역 · 관광 = 출발 → 도착 · 출발일. */
export function quoteSummary(q: Pick<Quote, 'kind' | 'routeRegion' | 'departRegion' | 'arriveRegion' | 'departDate'>): string {
  if (q.kind === 'commute') return (q.routeRegion || '').trim() || '(노선 지역 미입력)';
  const from = (q.departRegion || '').trim();
  const to = (q.arriveRegion || '').trim();
  const route = from || to ? `${from || '?'} → ${to || '?'}` : '(구간 미입력)';
  const date = (q.departDate || '').trim();
  return date ? `${route} · ${date}` : route;
}

/**
 * 견적을 받는 창구 거래처 — 위젯이 이 거래처에서만 「견적 문의」 버튼을 보인다.
 * ⚠️ functions `quote.ts` `QUOTE_TENANTS` 가 같은 목록을 미러한다(서버가 최종 거부) — 한쪽만 고치지 말 것.
 */
export const QUOTE_TENANTS: string[] = ['homepage'];

/** 구 시트 이관분이 「처리 필요」에 남는 기간 — 이보다 오래된 미결은 사실상 종결이라 목록을 덮지 않게 뺀다. */
export const QUOTE_MIGRATED_OPEN_MS = 90 * 24 * 3600_000;

/**
 * 「처리 필요」 판정 — 접수·상담중이면서, 이관분은 최근 90일 안의 것만.
 * 🔴 이관 767건 중 결과 칸이 빈 2017~ 행(관광 「입력」 167건 등)이 «접수» 로 들어온다 — 그걸 다 세면
 *    처리 필요 칸이 수백 건으로 차 정작 새 견적이 묻힌다. 단계 자체는 바꾸지 않는다(원문은 legacyResult).
 */
export function quoteNeedsAction(
  q: Pick<Quote, 'stage' | 'source'> & { createdAt?: TimestampLike },
  nowMs: number,
): boolean {
  if (!QUOTE_OPEN_STAGES.includes(q.stage)) return false;
  if (q.source !== 'migrated') return true;
  const at = q.createdAt?.toDate().getTime() ?? 0;
  return nowMs - at < QUOTE_MIGRATED_OPEN_MS;
}
